import React from 'react';
import { useDrag } from 'react-dnd';
import Card from './ui/Card';
import { getColorForCourse } from '../utils/colorUtils';
import { HelpIcon } from './Documentation';
import { SlDrawer } from 'react-icons/sl';

// DnD item types - replicated from ClassSchedulerPage
const ItemTypes = {
    COURSE: 'course',
    SCHEDULED_ITEM: 'scheduled_item'
};

// DraggableCourseItem - moved here from ClassSchedulerPage
const DraggableCourseItem = ({ courseDefinition, onDragStartInternal, onDelete }) => {
    const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
    const [isHovered, setIsHovered] = React.useState(false);

    const [{ isDragging }, drag] = useDrag(() => ({
        type: ItemTypes.SCHEDULED_ITEM,
        item: () => {
            // Create the drag item data with typeDnd
            const serializedData = typeof courseDefinition.toObject === 'function'
                ? courseDefinition.toObject()
                : { ...courseDefinition };
            const dragItemData = { ...serializedData, typeDnd: ItemTypes.SCHEDULED_ITEM };

            // Pass the drag item data (with typeDnd) to the global drag handler
            onDragStartInternal(dragItemData, 0);

            return dragItemData;
        },
        collect: (monitor) => ({
            isDragging: !!monitor.isDragging(),
        }),
    }));

    const colorClass = getColorForCourse(courseDefinition.course || courseDefinition.type);

    // Helper function to format groups for display
    const getGroupDisplayText = () => {
        if (!courseDefinition.groups || courseDefinition.groups.length === 0) {
            return 'All Groups';
        }

        // Handle groups as objects with name/group_code or as strings/numbers
        return courseDefinition.groups.map(group => {
            if (typeof group === 'object' && group !== null) {
                return group.name || group.group_code || group.id?.toString() || 'Unknown';
            }
            return group.toString();
        }).join(', ');
    };

    // Handle native HTML5 drag start to set dataTransfer data
    const handleDragStart = (e) => {
        console.log('[DraggableCourseItem] Native drag start for ID:', courseDefinition.id);
        e.dataTransfer.setData('text/plain', courseDefinition.id);
        // Set effectAllowed to enable dropping
        e.dataTransfer.effectAllowed = 'move';
    };

    // Handle native drag end
    const handleDragEnd = (e) => {
        console.log('[DraggableCourseItem] Native drag end for ID:', courseDefinition.id, 'dropEffect:', e.dataTransfer.dropEffect);
    };

    // Handle delete confirmation
    const handleDeleteClick = (e) => {
        e.stopPropagation();
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = async (e) => {
        e.stopPropagation();
        if (onDelete) {
            try {
                await onDelete(courseDefinition);
            } catch (error) {
                console.error('Failed to delete unscheduled item:', error);
            }
        }
        setShowDeleteConfirm(false);
    };

    const handleDeleteCancel = (e) => {
        e.stopPropagation();
        setShowDeleteConfirm(false);
    };

    return (
        <div
            ref={drag}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className={`p-2 mb-2 rounded-md shadow-sm border cursor-grab relative ${colorClass} ${isDragging ? 'opacity-50 ring-2 ring-blue-500' : 'opacity-100'}`}
            title={`Drag to schedule: ${courseDefinition.course}\nTeacher: ${courseDefinition.teacher}\nGroups: ${getGroupDisplayText()}`}
        >
            {showDeleteConfirm ? (
                // Confirmation UI
                <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-red-600">Delete?</span>
                    <div className="flex items-center space-x-1">
                        <button
                            onClick={handleDeleteCancel}
                            className="text-gray-500 hover:text-gray-700 text-xs font-bold px-1 py-0.5 rounded hover:bg-gray-200"
                            title="Cancel"
                        >
                            ×
                        </button>
                        <button
                            onClick={handleDeleteConfirm}
                            className="text-green-600 hover:text-green-800 text-xs font-bold px-1 py-0.5 rounded hover:bg-green-100"
                            title="Confirm Delete"
                        >
                            ✓
                        </button>
                    </div>
                </div>
            ) : (
                // Normal content
                <>
                    <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                            <p className="font-semibold text-xs truncate">{courseDefinition.course || courseDefinition.type?.toUpperCase()}</p>
                            <p className="text-[10px] truncate opacity-80">{courseDefinition.teacher}</p>
                            <p className="text-[10px] truncate opacity-70">{getGroupDisplayText()}</p>
                        </div>
                        {/* Trash icon - only show on hover */}
                        {isHovered && onDelete && (
                            <button
                                onClick={handleDeleteClick}
                                className="ml-2 text-gray-400 hover:text-red-500 text-xs opacity-70 hover:opacity-100 transition-all duration-200"
                                title="Delete this course"
                            >
                                🗑️
                            </button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

const UnscheduledEntryContainer = ({
    unscheduledCourses,
    onDragStartInternal,
    onRemoveScheduledItem,
    onDeleteUnscheduled,
    draggedItemInfo
}) => {
    const [isOver, setIsOver] = React.useState(false);
    const [canDrop, setCanDrop] = React.useState(false);
    const containerRef = React.useRef(null);

    // Return true if we're currently dragging a scheduled item that can be dropped
    const isDraggingScheduledItem = draggedItemInfo &&
        draggedItemInfo.entry &&
        draggedItemInfo.typeDnd === ItemTypes.SCHEDULED_ITEM;

    const handleDragOver = React.useCallback((e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setIsOver(true);
        setCanDrop(true);
    }, []);

    const handleDragLeave = React.useCallback((e) => {
        if (containerRef.current && !containerRef.current.contains(e.relatedTarget)) {
            setIsOver(false);
            setCanDrop(false);
        }
    }, []);

    const handleDrop = React.useCallback((e) => {
        e.preventDefault();
        setIsOver(false);
        setCanDrop(false);

        // Try to get the item ID from the data transfer
        const itemId = e.dataTransfer.getData('text/plain');
        if (!itemId) {
            console.error('[UnscheduledEntryContainer] No item ID found in drop data');
            return;
        }

        console.log('[UnscheduledEntryContainer] Drop detected with ID:', itemId);
        onRemoveScheduledItem(itemId);
    }, [onRemoveScheduledItem]);

    const dropIndicatorClass = isOver && canDrop
        ? 'bg-green-50 ring-2 ring-green-300'
        : isOver
            ? 'bg-gray-100 ring-2 ring-blue-300'
            : isDraggingScheduledItem
                ? 'ring-2 ring-blue-100 bg-blue-50/30'
                : '';

    return (
        <Card
            ref={containerRef}
            className={`flex-shrink-0 h-full flex flex-col shadow-lg transition-all ${dropIndicatorClass}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            <h3 className="text-sm font-semibold p-2 border-b bg-gray-50 text-gray-700 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                    <SlDrawer className="w-4 h-4 text-gray-600" />
                    <span>Unscheduled Courses</span>
                    <HelpIcon
                        documentationId="schedule-unscheduled-container"
                        position="inline"
                        size={14}
                        variant="info"
                    />
                </div>
                <div className="flex items-center space-x-2">
                    {isOver && canDrop && (
                        <span className="text-xs font-normal text-green-600">Drop to unschedule</span>
                    )}
                    {isDraggingScheduledItem && !isOver && (
                        <span className="text-xs font-normal text-blue-500">Drag here to unschedule</span>
                    )}
                </div>
            </h3>
            <div className="flex-grow p-2 overflow-y-auto">
                {unscheduledCourses.length > 0 ? (
                    unscheduledCourses.map(courseDef => (
                        <DraggableCourseItem
                            key={courseDef.id}
                            courseDefinition={courseDef}
                            onDragStartInternal={onDragStartInternal}
                            onDelete={onDeleteUnscheduled}
                        />
                    ))
                ) : (
                    <div className="text-xs text-gray-500 text-center py-4">
                        <p>No unscheduled courses available.</p>
                        <p className="mt-2 text-blue-500">
                            Drag a scheduled item here to unschedule it.
                        </p>
                        {isDraggingScheduledItem && !isOver && (
                            <div className="mt-3 p-4 border-2 border-dashed border-blue-300 rounded-md bg-blue-50 flex items-center justify-center">
                                <p className="font-medium text-blue-600">Drop here to unschedule</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Card>
    );
};

export default UnscheduledEntryContainer; 